import { useState } from 'react';
import type { SamplingPolicyInput, SamplingScope } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Button, Checkbox, Dialog, FormField, Input, RadioGroup, Select } from '../../ui/index.ts';
import {
  SAMPLING_VALUE_FIELDS,
  SCOPE_DISPLAY_ORDER,
  isTraceOnlyField,
  parseDraft,
  samplingFieldKind,
  scopeNeedsLane,
  scopeNeedsOrg,
  scopeNeedsSku,
  splitLanes,
} from './samplingPolicyFields.ts';
import type {
  SamplingDraftError,
  SamplingDraftErrors,
  SamplingPolicyDraft,
  SamplingValueField,
} from './samplingPolicyFields.ts';

export const SCOPE_LABEL: Record<SamplingScope, I18nKey> = {
  region: 'tenant_routing.load.sampling.scope.region',
  sku: 'tenant_routing.load.sampling.scope.sku',
  org: 'tenant_routing.load.sampling.scope.org',
  org_sku: 'tenant_routing.load.sampling.scope.org_sku',
  lane: 'tenant_routing.load.sampling.scope.lane',
  org_lane: 'tenant_routing.load.sampling.scope.org_lane',
};

export const VALUE_FIELD_LABEL: Record<SamplingValueField, I18nKey> = {
  keep_ratio: 'tenant_routing.load.sampling.field.keep_ratio',
  keep_slow_ms: 'tenant_routing.load.sampling.field.keep_slow_ms',
  size_threshold_bytes: 'tenant_routing.load.sampling.field.size_threshold_bytes',
  large_full_keep_ratio: 'tenant_routing.load.sampling.field.large_full_keep_ratio',
};

const VALUE_FIELD_HINT: Record<SamplingValueField, I18nKey> = {
  keep_ratio: 'tenant_routing.load.sampling.field.keep_ratio_hint',
  keep_slow_ms: 'tenant_routing.load.sampling.field.keep_slow_ms_hint',
  size_threshold_bytes: 'tenant_routing.load.sampling.field.size_threshold_bytes_hint',
  large_full_keep_ratio: 'tenant_routing.load.sampling.field.large_full_keep_ratio_hint',
};

const ERROR_COPY: Record<SamplingDraftError, I18nKey> = {
  org_required: 'tenant_routing.load.sampling.error.org_required',
  sku_required: 'tenant_routing.load.sampling.error.sku_required',
  lane_required: 'tenant_routing.load.sampling.error.lane_required',
  invalid_lane: 'tenant_routing.load.sampling.error.invalid_lane',
  invalid_ratio: 'tenant_routing.load.sampling.error.invalid_ratio',
  invalid_int: 'tenant_routing.load.sampling.error.invalid_int',
  duplicate: 'tenant_routing.load.sampling.error.duplicate',
};

/**
 * Add / edit sampling-policy rows. Applying only changes the section's
 * local draft; nothing is written until the section's save button PUTs the
 * whole set. A lane scope with N picked lanes applies N rows of equal values.
 */
export function SamplingPolicyDialog({
  isNew,
  initial,
  otherKeys,
  laneOptions,
  onCancel,
  onApply,
}: {
  readonly isNew: boolean;
  readonly initial: SamplingPolicyDraft;
  readonly otherKeys: ReadonlySet<string>;
  /** Namespaces the gateway's SKU catalog offers; empty = it could not be read. */
  readonly laneOptions: readonly string[];
  readonly onCancel: () => void;
  readonly onApply: (rows: readonly SamplingPolicyInput[]) => void;
}): JSX.Element {
  const t = useT();
  const [draft, setDraft] = useState<SamplingPolicyDraft>(initial);
  const [errors, setErrors] = useState<SamplingDraftErrors>({});
  const [laneText, setLaneText] = useState(initial.lanes.join(', '));
  const needsOrg = scopeNeedsOrg(draft.scope);
  const needsSku = scopeNeedsSku(draft.scope);
  const needsLane = scopeNeedsLane(draft.scope);
  // A lane being edited stays pickable even when the catalog no longer lists it.
  // Checkbox picker only when the catalog was readable; otherwise free text.
  const hasCatalog = laneOptions.length > 0;
  const laneChoices = hasCatalog ? [...new Set([...laneOptions, ...draft.lanes])].sort() : [];

  function toggleLane(lane: string, checked: boolean): void {
    setDraft((prev) => ({
      ...prev,
      lanes: checked
        ? (prev.lanes.includes(lane) ? prev.lanes : [...prev.lanes, lane])
        : prev.lanes.filter((picked) => picked !== lane),
    }));
  }

  function update(patch: Partial<SamplingPolicyDraft>): void {
    setDraft((prev) => ({ ...prev, ...patch }));
  }

  function errorText(error: SamplingDraftError | undefined): string | undefined {
    return error === undefined ? undefined : t(ERROR_COPY[error]);
  }

  function handleApply(): void {
    const parsed = parseDraft(draft, otherKeys);
    if (!parsed.ok) {
      setErrors(parsed.errors);
      return;
    }
    setErrors({});
    onApply(parsed.rows);
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      size="lg"
      title={t(isNew ? 'tenant_routing.load.sampling.dialog.add_title' : 'tenant_routing.load.sampling.dialog.edit_title')}
      description={t('tenant_routing.load.sampling.dialog.hint')}
      closeLabel={t('common.close')}
      testId="sampling-policy-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" onClick={handleApply} data-testid="sampling-policy-dialog-apply">
            {t('tenant_routing.load.sampling.dialog.apply')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label={t('tenant_routing.load.sampling.field.stream')} fieldId="sampling-policy-stream">
            <RadioGroup
              value={draft.stream}
              onValueChange={(next) => {
                if (next === 'trace' || next === 'lifecycle') update({ stream: next });
              }}
              options={[
                { value: 'trace', label: t('tenant_routing.load.sampling.stream.trace') },
                { value: 'lifecycle', label: t('tenant_routing.load.sampling.stream.lifecycle') },
              ]}
              orientation="horizontal"
              ariaLabel={t('tenant_routing.load.sampling.field.stream')}
              testId="sampling-policy-stream"
            />
          </FormField>
          <FormField label={t('tenant_routing.load.sampling.field.region')} fieldId="sampling-policy-region">
            <RadioGroup
              value={draft.region}
              onValueChange={(next) => {
                if (next === 'jp' || next === 'cn') update({ region: next });
              }}
              options={[
                { value: 'jp', label: 'JP' },
                { value: 'cn', label: 'CN' },
              ]}
              orientation="horizontal"
              ariaLabel={t('tenant_routing.load.sampling.field.region')}
              testId="sampling-policy-region"
            />
          </FormField>
        </div>

        <section className="flex flex-col gap-4 rounded-lg border border-line-soft bg-well/40 p-4">
          <FormField
            label={t('tenant_routing.load.sampling.field.scope')}
            fieldId="sampling-policy-scope"
            hint={t('tenant_routing.load.sampling.field.scope_hint')}
            error={errorText(errors.scope)}
          >
            <Select
              id="sampling-policy-scope"
              value={draft.scope}
              onValueChange={(next) => {
                const scope = SCOPE_DISPLAY_ORDER.find((candidate) => candidate === next);
                if (scope !== undefined) update({ scope });
              }}
              options={SCOPE_DISPLAY_ORDER.map((scope) => ({ value: scope, label: t(SCOPE_LABEL[scope]) }))}
              invalid={errors.scope !== undefined}
              testId="sampling-policy-scope"
            />
          </FormField>

          {needsOrg || needsSku ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {needsOrg ? (
                <FormField
                  label={t('tenant_routing.load.sampling.field.org_id')}
                  fieldId="sampling-policy-org_id"
                  required
                  error={errorText(errors.org_id)}
                >
                  <Input
                    id="sampling-policy-org_id"
                    mono
                    value={draft.org_id}
                    invalid={errors.org_id !== undefined}
                    aria-describedby={errors.org_id !== undefined ? 'sampling-policy-org_id-error' : undefined}
                    onChange={(event) => update({ org_id: event.target.value })}
                    data-testid="sampling-policy-org_id"
                  />
                </FormField>
              ) : null}
              {needsSku ? (
                <FormField
                  label={t('tenant_routing.load.sampling.field.sku_id')}
                  fieldId="sampling-policy-sku_id"
                  required
                  error={errorText(errors.sku_id)}
                >
                  <Input
                    id="sampling-policy-sku_id"
                    mono
                    value={draft.sku_id}
                    invalid={errors.sku_id !== undefined}
                    aria-describedby={errors.sku_id !== undefined ? 'sampling-policy-sku_id-error' : undefined}
                    onChange={(event) => update({ sku_id: event.target.value })}
                    data-testid="sampling-policy-sku_id"
                  />
                </FormField>
              ) : null}
            </div>
          ) : null}

          {needsLane ? (
            <FormField
              label={t('tenant_routing.load.sampling.field.lanes')}
              fieldId="sampling-policy-lanes"
              required
              hint={t(hasCatalog
                ? 'tenant_routing.load.sampling.field.lanes_hint'
                : 'tenant_routing.load.sampling.field.lanes_free')}
              error={errorText(errors.lanes)}
            >
              {hasCatalog ? (
                <div
                  id="sampling-policy-lanes"
                  role="group"
                  aria-label={t('tenant_routing.load.sampling.field.lanes')}
                  className="grid gap-x-4 gap-y-2 sm:grid-cols-2"
                  data-testid="sampling-policy-lanes"
                >
                  {laneChoices.map((lane) => (
                    <Checkbox
                      key={lane}
                      checked={draft.lanes.includes(lane)}
                      onCheckedChange={(checked) => toggleLane(lane, checked)}
                      label={<span className="font-mono text-dense">{lane}</span>}
                      testId={`sampling-policy-lane-${lane}`}
                    />
                  ))}
                </div>
              ) : (
                <Input
                  id="sampling-policy-lanes"
                  mono
                  value={laneText}
                  invalid={errors.lanes !== undefined}
                  aria-describedby={errors.lanes !== undefined ? 'sampling-policy-lanes-error' : undefined}
                  onChange={(event) => {
                    setLaneText(event.target.value);
                    update({ lanes: splitLanes(event.target.value) });
                  }}
                  data-testid="sampling-policy-lanes-text"
                />
              )}
            </FormField>
          ) : null}
        </section>

        <h4 className="m-0 text-label text-paper">{t('tenant_routing.load.sampling.field.values_title')}</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          {SAMPLING_VALUE_FIELDS.map((field) => {
            const traceOnlyOff = draft.stream !== 'trace' && isTraceOnlyField(field);
            const fieldId = `sampling-policy-${field}`;
            return (
              <FormField
                key={field}
                label={t(VALUE_FIELD_LABEL[field])}
                fieldId={fieldId}
                hint={t(traceOnlyOff ? 'tenant_routing.load.sampling.field.trace_only' : VALUE_FIELD_HINT[field])}
                error={errorText(errors[field])}
              >
                <Input
                  id={fieldId}
                  mono
                  inputMode={samplingFieldKind(field) === 'ratio' ? 'decimal' : 'numeric'}
                  placeholder={traceOnlyOff ? '' : t('tenant_routing.load.sampling.inherit')}
                  value={traceOnlyOff ? '' : draft[field]}
                  disabled={traceOnlyOff}
                  invalid={errors[field] !== undefined}
                  aria-describedby={errors[field] !== undefined ? `${fieldId}-error` : undefined}
                  onChange={(event) => update({ [field]: event.target.value })}
                  data-testid={fieldId}
                />
              </FormField>
            );
          })}
        </div>

        <FormField label={t('tenant_routing.load.sampling.field.note')} fieldId="sampling-policy-note">
          <Input
            id="sampling-policy-note"
            value={draft.note}
            onChange={(event) => update({ note: event.target.value })}
            data-testid="sampling-policy-note"
          />
        </FormField>
      </div>
    </Dialog>
  );
}
